import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { useApi, money, fmtDate, fmtTime } from "@/lib/api";
import { useMe } from "@/context/Me";
import { PageTitle, Empty, StatusPill, Stars, CountUp } from "@/components/bits";

export function Notifications() {
  const api = useApi();
  const { refresh } = useMe();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/notifications").then((r) => setList(r.data)); }, [api]);
  const readAll = async () => { await api.post("/notifications/read"); setList(list.map((n) => ({ ...n, read: true }))); refresh(); };
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <PageTitle eyebrow="Avisos" title="Notificações">
        <button data-testid="notifications-read-all" className="btn-ghost px-4 py-2 text-sm" onClick={readAll}><CheckCheck size={16} />Marcar como lidas</button>
      </PageTitle>
      {!list.length ? <Empty icon={Bell} title="Tudo em dia" text="Você ainda não tem notificações." /> : (
        <div className="space-y-2">{list.map((n) => (
          <Link key={n.id} to={n.link} data-testid={`notification-${n.id}`} className={`panel panel-hover flex items-start gap-3 p-4 ${n.read ? "opacity-60" : ""}`}>
            <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${n.read ? "bg-slate-600" : "bg-[#FF5722]"}`} />
            <div className="flex-1"><div className="font-semibold">{n.text}</div><div className="text-xs text-slate-500">{fmtTime(n.created_at)}</div></div>
          </Link>
        ))}</div>
      )}
    </div>
  );
}

const Kpi = ({ label, value, fmt, testid }) => (
  <div className="panel p-5"><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div><div className="mt-1 font-display text-4xl font-black text-[#FFC700]"><CountUp value={value || 0} format={fmt} testid={testid} /></div></div>
);

export function Reports() {
  const api = useApi();
  const { me } = useMe();
  const [month, setMonth] = useState("");
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports/me", { params: { month: month || undefined } }).then((x) => setR(x.data)); }, [api, month]);
  if (!r) return null;
  const c = me.country;
  const m = (v) => money(v, c);
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <PageTitle eyebrow="Relatórios" title={r.type === "pro" ? "Seu desempenho" : "Seus serviços"}>
        <input type="month" data-testid="reports-month" className="field w-auto" value={month} onChange={(e) => setMonth(e.target.value)} />
      </PageTitle>
      {r.type === "client" ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi label="Chamados" value={r.jobs.length} testid="report-jobs" />
            <Kpi label="Abertos" value={r.by_status.open} />
            <Kpi label="Em andamento" value={r.by_status.assigned} />
            <Kpi label="Concluídos" value={r.by_status.completed} />
            <Kpi label="Valores combinados" value={r.total_agreed} fmt={m} testid="report-total" />
          </div>
          <div className="panel mt-6 overflow-x-auto p-5">
            <table className="w-full text-sm"><thead><tr className="border-b border-white/10 text-left text-xs uppercase text-slate-400"><th className="py-2">Chamado</th><th>Status</th><th>Data</th><th className="text-right">Valor</th></tr></thead>
              <tbody>{r.jobs.map((j) => <tr key={j.id} className="border-b border-white/5"><td className="py-2.5"><Link to={`/chamados/${j.id}`} className="hover:text-[#FFC700]">{j.title}</Link></td><td><StatusPill s={j.status} /></td><td>{fmtDate(j.created_at)}</td><td className="text-right font-mono">{j.agreed_price ? m(j.agreed_price) : "—"}</td></tr>)}</tbody></table>
            <p className="mt-3 text-xs text-slate-500">Avaliações feitas: {r.reviews_given}</p>
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi label="Propostas enviadas" value={r.sent} testid="report-sent" />
            <Kpi label="Aceitas" value={r.accepted} />
            <Kpi label="Concluídos" value={r.completed} />
            <Kpi label="Valores combinados" value={r.total_agreed} fmt={m} />
            <div className="panel p-5"><div className="text-xs uppercase tracking-wider text-slate-400">Nota média</div><div className="mt-2"><Stars value={r.rating_avg} count={r.rating_count || 0} /></div></div>
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="panel overflow-x-auto p-5">
              <h3 className="mb-3 text-xl font-bold uppercase">Propostas</h3>
              <table className="w-full text-sm"><tbody>{r.proposals.map((p) => <tr key={p.id} className="border-b border-white/5"><td className="py-2"><Link to={`/chamados/${p.job_id}`} className="hover:text-[#FFC700]">{fmtDate(p.created_at)}</Link></td><td><StatusPill s={p.status} /></td><td className="text-right font-mono">{m(p.price)}</td></tr>)}</tbody></table>
            </div>
            <div className="panel p-5">
              <h3 className="mb-3 text-xl font-bold uppercase">Plano e pagamentos</h3>
              <div className="mb-3 text-sm">Plano atual: <b className="text-purple-300">{r.plan === "pro" ? `Pro (até ${fmtDate(r.pro_until)})` : "Grátis"}</b>{r.featured_until && r.featured_until > new Date().toISOString() && <> · Destaque até {fmtDate(r.featured_until)}</>}</div>
              <table className="w-full text-sm"><tbody>{r.payments.map((p) => <tr key={p.session_id} className="border-b border-white/5"><td className="py-2">{fmtDate(p.created_at)}</td><td>{p.product === "pro" ? "Pro" : "Destaque"}</td><td className="text-right font-mono">{money(p.amount, p.currency === "eur" ? "PT" : "BR")}</td></tr>)}</tbody></table>
              {!r.payments.length && <p className="text-sm text-slate-500">Nenhum pagamento.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
